import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BufferGeometry,
  Color,
  Group,
  Intersection,
  LineSegments,
  Material,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Points,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
} from 'three';

import { isDefined } from '../../../../utils/variables';
import { sfmViewerColors } from '../../../../viewer/elements/design/colors';
import { getAttributePositionAtIndex } from '../../../../viewer/geodata/point-clouds/cesium-point-cloud/points/buffer-attribute/position-buffer-attribute';
import { PickingLine } from '../../../../viewer/tools/picking-line';
import { PickingSphere } from '../../../../viewer/tools/picking-sphere';
import {
  clamp,
  Coordinates,
  findPointOnLineFromHeight,
} from '../../../../viewer/utilities/math-helpers';
import { setRayCasterParamsBasedOnCamera } from '../../../../viewer/utilities/pick';
import { useObject3d } from '../../../hooks/three/use-object-3d';
import { useMousePosition } from '../../../hooks/use-mouse-position';
import { AlignerContext } from './aligner-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { PlaceMarkersContext } from './place-markers-state';

export type CursorIntersectionResult = {
  intersection: Intersection;
  localPosition: Vector3;
  mousePosition: ReturnType<typeof useMousePosition>;
  coordinates: Coordinates;
};

/**
 * A cursor that snaps to all objects in the scene, defaulting to height 0 when not snapping.
 */
export function useCursor(
  ref?: RefObject<HTMLDivElement>,
  scene?: Scene,
  camera?: PerspectiveCamera | OrthographicCamera,
  gcpPoints?: Points,
  cameraGroup?: Group,
) {
  const { origo, getImageLocalPosition } = useContext(GeodataSpatialReferenceContext);
  const cameraPoints = useMemo(() => {
    return cameraGroup?.children.find((x) => x instanceof Points) as Points | undefined;
  }, [cameraGroup]);

  const { legendsMode } = useContext(AlignerContext);
  const raycaster = useRef(new Raycaster());
  const cursor = useRef(
    new PickingSphere(new Color(...sfmViewerColors.cursors.cursor), 11, 'round'),
  );
  const gcpCursor = useRef(
    new PickingSphere(new Color(...sfmViewerColors.cursors.gcpCursor), 12, 'round'),
  );
  const pinnedImageCursors = useRef(
    new PickingSphere(new Color(...sfmViewerColors.cursors.pinnedImageCursors), 16, 'cross'),
  );
  const unpinnedImageCursors = useRef(
    new PickingSphere(new Color(...sfmViewerColors.cursors.unpinnedImageCursors), 16, 'cross'),
  );
  const pinnedImageLines = useRef(
    new PickingLine(new Color(...sfmViewerColors.cursors.pinnedImageCursors)),
  );
  const unpinnedImageLines = useRef(
    new PickingLine(new Color(...sfmViewerColors.cursors.unpinnedImageCursors)),
  );
  const mousePosition = useMousePosition(ref);
  const { imagesWithMarkers, gcp } = useContext(PlaceMarkersContext);
  const [intersection, setIntersection] = useState<CursorIntersectionResult>();

  useObject3d(scene, cursor.current);
  useObject3d(scene, gcpCursor.current);
  useObject3d(scene, pinnedImageCursors.current);
  useObject3d(scene, unpinnedImageCursors.current);
  useObject3d(scene, pinnedImageLines.current);
  useObject3d(scene, unpinnedImageLines.current);

  useEffect(() => {
    gcpCursor.current.visible = isDefined(gcp);

    if (isDefined(gcp) && isDefined(origo) && isDefined(getImageLocalPosition)) {
      gcpCursor.current.position.set(
        gcp.enu[0] - origo.x,
        gcp.enu[1] - origo.y,
        gcp.enu[2] - origo.z,
      );
      if (legendsMode !== 'default') {
        pinnedImageCursors.current.visible = false;
        unpinnedImageCursors.current.visible = false;
        pinnedImageLines.current.visible = false;
        unpinnedImageLines.current.visible = false;
        return;
      }

      const pinnedPositions = imagesWithMarkers
        .filter((image) =>
          image.markers.some((marker) => {
            if (marker.gcpId !== gcp.id) {
              return false;
            }
            const estimates = marker.estimates ?? [];
            if (estimates.some((e) => e.status === 'ACCEPTED')) {
              return true;
            }
            if (estimates.some((e) => e.status === 'PENDING')) {
              return false;
            }
            return marker.pinned === true;
          }),
        )
        .map((image) => getImageLocalPosition(image.transform!));

      pinnedImageCursors.current.visible = pinnedPositions.length > 0;
      pinnedImageCursors.current.setNonIndexedVertices(pinnedPositions);

      const gcpLocalPosition = new Vector3(
        gcp.enu[0] - origo.x,
        gcp.enu[1] - origo.y,
        gcp.enu[2] - origo.z,
      );

      pinnedImageLines.current.visible = pinnedPositions.length > 0;
      if (pinnedPositions.length > 0) {
        pinnedImageLines.current.setNonIndexedVertices(
          pinnedPositions.flatMap((p) => [p, gcpLocalPosition]),
        );
      }

      const unpinnedPositions = imagesWithMarkers
        .filter((image) =>
          image.markers.some((marker) => {
            if (marker.gcpId !== gcp.id) {
              return false;
            }
            const estimates = marker.estimates ?? [];
            if (estimates.some((e) => e.status === 'ACCEPTED')) {
              return false;
            }
            if (estimates.some((e) => e.status === 'PENDING')) {
              return true;
            }
            return marker.pinned !== true;
          }),
        )
        .map((image) => getImageLocalPosition(image.transform!));

      unpinnedImageCursors.current.visible = unpinnedPositions.length > 0;
      unpinnedImageCursors.current.setNonIndexedVertices(unpinnedPositions);

      unpinnedImageLines.current.visible = unpinnedPositions.length > 0;
      if (unpinnedPositions.length > 0) {
        unpinnedImageLines.current.setNonIndexedVertices(
          unpinnedPositions.flatMap((p) => [p, gcpLocalPosition]),
        );
      }
    }
  }, [gcp, imagesWithMarkers, legendsMode, getImageLocalPosition, origo]);

  // Helper function to check if an object is a cursor object
  const isCursorObject = useCallback((obj: Object3D): boolean => {
    return (
      obj === cursor.current ||
      obj === gcpCursor.current ||
      obj === pinnedImageCursors.current ||
      obj === unpinnedImageCursors.current ||
      obj instanceof LineSegments
    );
  }, []);

  const lastPositionRef = useRef<Vector3 | undefined>();

  // Reusable temp vectors to avoid allocations
  const tmpVec = useRef(new Vector3());
  const tmpVec2 = useRef(new Vector3());

  // Cache DOM size to avoid getClientRects() calls
  const domSizeRef = useRef({ width: 1, height: 1 });

  // Iterate all direct children in the scene, skipping cursor objects and invisible objects
  const iterateAllObjects = useCallback(
    (callback: (obj: Object3D) => void) => {
      if (!isDefined(scene)) {
        return;
      }

      for (const child of scene.children) {
        // Skip cursor objects
        if (isCursorObject(child)) {
          continue;
        }

        // Skip invisible objects
        if (!child.visible) {
          continue;
        }

        callback(child);
      }
    },
    [scene, isCursorObject],
  );

  // Update DOM size cache via ResizeObserver
  useEffect(() => {
    if (!isDefined(ref?.current)) {
      return;
    }

    const element = ref.current;
    const updateSize = () => {
      domSizeRef.current = {
        width: element.clientWidth || 1,
        height: element.clientHeight || 1,
      };
    };

    updateSize(); // Initial size
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  useEffect(() => {
    if (
      !isDefined(ref?.current) ||
      !isDefined(mousePosition) ||
      !isDefined(scene) ||
      !isDefined(camera)
    ) {
      return;
    }

    raycaster.current.setFromCamera(new Vector2(mousePosition.ndc.x, mousePosition.ndc.y), camera);
    raycaster.current.params.Points = { threshold: 2 };

    // Use cached DOM size instead of getClientRects()
    const domSize = domSizeRef.current;
    setRayCasterParamsBasedOnCamera(
      camera,
      raycaster.current,
      20,
      clamp(cursor.current.position.distanceTo(raycaster.current.ray.origin), 0.1, 350),
      domSize.width,
      domSize.height,
    );

    // Lower threshold for smoother state updates

    let newPosition: Vector3 | undefined;
    let newIntersection: Intersection | undefined;
    let foundIntersection = false;
    let bestDistanceToRay = Infinity;

    // Helper function to get closest point from a Points object
    const getClosestPoint = (pointsObject: Points<BufferGeometry, Material>) => {
      const intersections = raycaster.current.intersectObjects([pointsObject]);

      for (const intersection of intersections) {
        // Skip intersections with cursor objects
        if (isCursorObject(intersection.object)) {
          continue;
        }

        if (isDefined(intersection.index)) {
          // getAttributePositionAtIndex creates a new Vector3, but we'll reuse tmpVec2 for localToWorld
          // Pass zero offset to avoid adding unwanted offset
          tmpVec.current.set(0, 0, 0);
          const localPos = getAttributePositionAtIndex(
            pointsObject,
            intersection.index,
            tmpVec.current,
          );
          // localToWorld mutates the input vector, so copy to tmpVec2 first
          tmpVec2.current.copy(localPos);
          const actualPosition = pointsObject.localToWorld(tmpVec2.current);

          // Calculate distance from ray to actual point position
          const distanceToRay = raycaster.current.ray.distanceToPoint(actualPosition);

          // Track best candidate in single pass
          if (distanceToRay < bestDistanceToRay) {
            bestDistanceToRay = distanceToRay;
            // Clone only the best candidate
            if (!isDefined(newPosition)) {
              newPosition = new Vector3();
            }
            newPosition.copy(actualPosition);
            newIntersection = intersection;
            foundIntersection = true;
          }
        }
      }
    };

    // Single-pass min finding: track best candidate without sorting
    // First, check prioritized geometry objects (GCP points and camera points)
    if (isDefined(gcpPoints)) {
      getClosestPoint(gcpPoints as Points<BufferGeometry, Material>);
    }

    if (!foundIntersection && isDefined(cameraPoints)) {
      getClosestPoint(cameraPoints as Points<BufferGeometry, Material>);
    }

    // If no intersection found, check other objects
    if (!foundIntersection) {
      const otherObjects: Object3D[] = [];

      for (const child of scene.children) {
        // Skip cursor objects
        if (isCursorObject(child)) {
          continue;
        }

        // Skip invisible objects
        if (!child.visible) {
          continue;
        }

        otherObjects.push(child);
      }

      for (const obj of otherObjects) {
        // Handle Points objects in "other" category
        if (obj instanceof Points && obj.geometry instanceof BufferGeometry) {
          getClosestPoint(obj as Points<BufferGeometry, Material>);
        } else if (isDefined(obj)) {
          // Handle non-Points meshes
          const meshIntersections = raycaster.current.intersectObjects([obj], true);

          for (const intersection of meshIntersections) {
            // Skip intersections with cursor objects
            if (isCursorObject(intersection.object)) {
              continue;
            }

            if (isDefined(intersection.point)) {
              const distanceToRay = raycaster.current.ray.distanceToPoint(intersection.point);

              // Track best candidate in single pass
              if (distanceToRay < bestDistanceToRay) {
                bestDistanceToRay = distanceToRay;
                // Clone only the best candidate
                if (!isDefined(newPosition)) {
                  newPosition = new Vector3();
                }
                newPosition.copy(intersection.point);
                newIntersection = intersection;
                foundIntersection = true;
              }
            }
          }
        }
      }
    }

    // If no intersection found, default to height 0
    if (!foundIntersection) {
      const pointAtHeight0 = findPointOnLineFromHeight(
        raycaster.current.ray.origin,
        raycaster.current.ray.direction,
        0,
      );

      if (isDefined(pointAtHeight0)) {
        if (!isDefined(newPosition)) {
          newPosition = new Vector3();
        }
        newPosition.copy(pointAtHeight0);
      } else {
        // Reuse temp vectors instead of cloning
        tmpVec.current.copy(raycaster.current.ray.direction).multiplyScalar(100);
        if (!isDefined(newPosition)) {
          newPosition = new Vector3();
        }
        newPosition.copy(raycaster.current.ray.origin).add(tmpVec.current);
      }
    }

    // Always update cursor position immediately for smooth visual feedback
    if (isDefined(newPosition)) {
      cursor.current.position.copy(newPosition);
      cursor.current.visible = true;
    }

    if (isDefined(newPosition)) {
      // Reuse ref vector instead of cloning
      if (!isDefined(lastPositionRef.current)) {
        lastPositionRef.current = new Vector3();
      }
      lastPositionRef.current.copy(newPosition);

      if (foundIntersection && isDefined(newIntersection) && isDefined(origo)) {
        setIntersection({
          intersection: newIntersection,
          localPosition: newPosition,
          coordinates: {
            easting: newPosition.x + origo.x,
            northing: newPosition.y + origo.y,
            height: newPosition.z + origo.z,
          },
          mousePosition: mousePosition,
        });
      } else {
        // No intersection - clear state
        setIntersection(undefined);
      }
    }
  }, [
    mousePosition,
    scene,
    camera,
    ref,
    isCursorObject,
    iterateAllObjects,
    gcpPoints,
    cameraPoints,
    origo,
  ]);

  return { intersection, cursorPosition: cursor.current.position };
}
